import { apiClient, apiCall } from './api';
import axios from 'axios';
import type {
  Company,
  CompanyUsage,
  CompanyCreateAdmin,
  PlanChangeRequest,
  StatusChangeRequest,
  PortalInfo,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export const portalApi = {
  /** Public endpoint — no auth required */
  async getPortalInfo(subdomain: string): Promise<PortalInfo> {
    const response = await axios.get<PortalInfo>(`${API_BASE_URL}/api/v1/portal/${subdomain}`);
    return response.data;
  },
};

export const companyApi = {
  async getCompany(): Promise<Company> {
    return apiCall(() => apiClient.get<Company>('/api/v1/company'));
  },

  async getCompanyUsage(): Promise<CompanyUsage> {
    return apiCall(() => apiClient.get<CompanyUsage>('/api/v1/company/usage'));
  },

  async getMyCompanyStats(): Promise<any> {
    return apiCall(() => apiClient.get<any>('/api/v1/company/stats'));
  },
};

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DashboardMetrics {
  companiesByStatus: {
    active: number;
    trial: number;
    suspended: number;
    cancelled: number;
  };
  usersByPlan: {
    starter: number;
    business: number;
    enterprise: number;
  };
  totalExpensesThisMonth: number;
  expenseCountThisMonth: number;
  newCompaniesLast30Days: number;
  topCompaniesByUsage: Array<{
    companyId: string;
    name: string;
    expenseCount: number;
    totalAmount: number;
  }>;
}

export const adminCompanyApi = {
  async listCompanies(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    planFilter?: string;
    statusFilter?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<PaginatedResponse<Company>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.pageSize) queryParams.append('page_size', params.pageSize.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.planFilter) queryParams.append('plan_filter', params.planFilter);
    if (params?.statusFilter) queryParams.append('status_filter', params.statusFilter);
    if (params?.sortBy) queryParams.append('sort_by', params.sortBy);
    if (params?.sortOrder) queryParams.append('sort_order', params.sortOrder);
    
    const url = `/api/v1/admin/companies${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiCall(() => apiClient.get<PaginatedResponse<Company>>(url));
  },

  async createCompany(data: CompanyCreateAdmin): Promise<Company> {
    return apiCall(() => apiClient.post<Company>('/api/v1/admin/companies', data));
  },

  async changePlan(companyId: string, data: PlanChangeRequest): Promise<Company> {
    return apiCall(() =>
      apiClient.put<Company>(`/api/v1/admin/companies/${companyId}/plan`, data)
    );
  },

  async changeStatus(companyId: string, data: StatusChangeRequest): Promise<Company> {
    return apiCall(() =>
      apiClient.put<Company>(`/api/v1/admin/companies/${companyId}/status`, data)
    );
  },

  async changePortal(companyId: string, data: { portalEnabled: boolean; customSubdomain?: string }): Promise<Company> {
    return apiCall(() =>
      apiClient.put<Company>(`/api/v1/admin/companies/${companyId}/portal`, data)
    );
  },

  async getCompanyStats(companyId: string): Promise<any> {
    return apiCall(() =>
      apiClient.get<any>(`/api/v1/admin/companies/${companyId}/stats`)
    );
  },

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    return apiCall(() =>
      apiClient.get<DashboardMetrics>('/api/v1/admin/dashboard/metrics')
    );
  },
};
